import { Stack, xcss } from '@atlaskit/primitives';
import ProfileCardClient from '@atlaskit/profilecard/client';
import ProfileCard from '@atlaskit/profilecard/user';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { UserCard } from '@databases/user';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { type ViewComponentProps } from './types';

const userContainerStyles = xcss({
  listStyle: 'none',
  padding: 'space.0',
  width: '100%',
  gap: 'space.025',
});

const resourceClient = new ProfileCardClient({
  url: '/gateway/api/directory/graphql',
  productIdentifier: 'confluence',
});

const UserValueView: FunctionComponent<ViewComponentProps<DatabaseFieldType.USER>> = ({
  value,
}) => {
  const { formatMessage } = useIntl();
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);

  const resolvedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((user) => {
        if (
          'accountId' in user &&
          user.accountId === USER_VALUE_CURRENT &&
          currentUserId
        ) {
          return { _id: user._id, accountId: currentUserId };
        } else {
          return user;
        }
      });
    } else {
      return [];
    }
  }, [currentUserId, value]);

  const createProfileAction = useCallback(
    (accountId: string) => ({
      label: formatMessage(messages['user-value-value.profile-card-view-profile-label']),
      callback: () =>
        window.open(
          `/wiki/people/${accountId}?ref=confluence`,
          '_blank',
          'noopener,noreferrer'
        ),
    }),
    [formatMessage]
  );

  return (
    <Stack as="ul" xcss={userContainerStyles}>
      {resolvedValue.filter(isNonEmptyUserValue).map((user) => (
        <li key={user._id}>
          <ProfileCard
            resourceClient={resourceClient}
            userId={user.accountId}
            actions={[createProfileAction(user.accountId)]}
            viewingUserId={currentUserId}
          >
            <UserCard
              accountId={user.accountId}
              currentUserId={currentUserId}
              variant="mention"
            />
          </ProfileCard>
        </li>
      ))}
    </Stack>
  );
};

export default UserValueView;
